import {
  GeoPoint,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  DEFAULT_MENU_CATALOG_OWNER,
  DEFAULT_ONLINE_ORDER_CONTROL_MODE,
  MenuCatalogOwner,
  OnlineOrderControlMode,
  ProductAvailabilityControlMode,
  Store,
  StoreFormData,
  normalizeMenuCatalogOwner,
  normalizeOnlineOrderControlMode,
  normalizeProductAvailabilityControlMode,
  toStore,
} from '../models/Store';

const storesCollectionRef = collection(db, 'stores');
const driversCollectionRef = collection(db, 'drivers');
const DRIVER_DELIVERY_SCOPE_BATCH_LIMIT = 400;

const listStoresSorted = async (): Promise<Store[]> => {
  const snapshot = await getDocs(query(storesCollectionRef, orderBy('name')));
  return snapshot.docs.map((storeDoc) => toStore(storeDoc));
};

const getStoreGroupId = (store: Partial<Store> | null | undefined): string | null =>
  store?.operationGroupId || null;

const normalizeOptionalString = (rawValue: unknown): string | null => {
  if (typeof rawValue !== 'string') {
    return null;
  }

  const trimmed = rawValue.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeStringArray = (rawValue: unknown): string[] => {
  if (!Array.isArray(rawValue)) {
    return [];
  }

  return Array.from(
    new Set(
      rawValue
        .map((value) => normalizeOptionalString(value))
        .filter((value): value is string => value !== null)
    )
  ).sort();
};

const sameStringArray = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const rebuildOperationGroupState = async (groupId: string | null | undefined): Promise<void> => {
  if (!groupId) {
    return;
  }

  const groupRef = doc(db, 'operationGroups', groupId);
  const groupDoc = await getDoc(groupRef);
  if (!groupDoc.exists()) {
    return;
  }

  const storesSnapshot = await getDocs(
    query(storesCollectionRef, where('operationGroupId', '==', groupId))
  );

  const stores = storesSnapshot.docs.map((storeDoc) => toStore(storeDoc));
  const deliveryResponsibleStore =
    stores.find((store) => store.isActive && store.isDeliveryResponsible) || null;

  await setDoc(
    groupRef,
    {
      storeIds: stores.map((store) => store.id),
      deliveryResponsibleStoreId: deliveryResponsibleStore?.id || null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

const refreshDriverDeliveryStoreIdsForGroup = async (
  groupId: string | null | undefined
): Promise<void> => {
  const normalizedGroupId = normalizeOptionalString(groupId);
  if (!normalizedGroupId) {
    return;
  }

  const groupDoc = await getDoc(doc(db, 'operationGroups', normalizedGroupId));
  const groupStoreIds = normalizeStringArray(groupDoc.data()?.storeIds);
  const driversSnapshot = await getDocs(
    query(driversCollectionRef, where('groupId', '==', normalizedGroupId))
  );

  const updates = driversSnapshot.docs
    .map((driverDoc) => {
      const data = driverDoc.data();
      const assignmentMode =
        data.assignmentMode === 'store_locked' ? 'store_locked' : 'group_shared';
      const assignedStoreId =
        normalizeOptionalString(data.assignedStoreId) || normalizeOptionalString(data.storeId);
      const nextDeliveryStoreIds =
        assignmentMode === 'store_locked'
          ? assignedStoreId
            ? [assignedStoreId]
            : []
          : groupStoreIds.length > 0
            ? groupStoreIds
            : assignedStoreId
              ? [assignedStoreId]
              : [];
      const currentDeliveryStoreIds = normalizeStringArray(data.deliveryStoreIds);

      if (sameStringArray(currentDeliveryStoreIds, nextDeliveryStoreIds)) {
        return null;
      }

      return {
        ref: driverDoc.ref,
        deliveryStoreIds: nextDeliveryStoreIds,
      };
    })
    .filter(
      (update): update is { ref: typeof driversSnapshot.docs[number]['ref']; deliveryStoreIds: string[] } =>
        update !== null
    );

  for (let index = 0; index < updates.length; index += DRIVER_DELIVERY_SCOPE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    updates.slice(index, index + DRIVER_DELIVERY_SCOPE_BATCH_LIMIT).forEach((update) => {
      batch.update(update.ref, {
        deliveryStoreIds: update.deliveryStoreIds,
        deliveryStoreIdsUpdatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }
};

const ensureActiveDeliveryResponsibleInternal = async (
  groupId: string | null | undefined
): Promise<void> => {
  const stores = await listStoresSorted();
  const scopedStores = stores.filter(
    (store) => getStoreGroupId(store) === (groupId || null)
  );

  if (scopedStores.some((store) => store.isActive && store.isDeliveryResponsible)) {
    await rebuildOperationGroupState(groupId);
    return;
  }

  const fallbackStore = scopedStores.find((store) => store.isActive) || null;
  if (!fallbackStore) {
    await rebuildOperationGroupState(groupId);
    return;
  }

  await updateDoc(doc(db, 'stores', fallbackStore.id), {
    isDeliveryResponsible: true,
    updatedAt: serverTimestamp(),
  });

  await rebuildOperationGroupState(groupId);
};

export const storeService = {
  getAllStores: async (): Promise<Store[]> => {
    try {
      return await listStoresSorted();
    } catch (error: any) {
      console.error('Error getting stores:', error);
      throw new Error(`Failed to fetch stores: ${error.message}`);
    }
  },

  getActiveStores: async (): Promise<Store[]> => {
    try {
      const stores = await listStoresSorted();
      return stores.filter((store) => store.isActive);
    } catch (error: any) {
      console.error('Error getting active stores:', error);
      throw new Error(`Failed to fetch active stores: ${error.message}`);
    }
  },

  getStoreById: async (id: string): Promise<Store | null> => {
    try {
      const storeDoc = await getDoc(doc(db, 'stores', id));
      if (storeDoc.exists()) {
        return toStore(storeDoc);
      }
      return null;
    } catch (error: any) {
      console.error(`Error getting store ${id}:`, error);
      throw new Error(`Failed to fetch store: ${error.message}`);
    }
  },

  createStore: async (storeData: StoreFormData): Promise<Store> => {
    try {
      const newStoreRef = doc(storesCollectionRef);
      const geoPoint = new GeoPoint(
        storeData.location.latitude,
        storeData.location.longitude
      );
      const groupId = storeData.operationGroupId || null;
      const isDeliveryResponsible = storeData.isActive
        ? storeData.isDeliveryResponsible === true
        : false;

      await setDoc(newStoreRef, {
        name: storeData.name,
        address: storeData.address,
        location: geoPoint,
        isActive: storeData.isActive,
        isDeliveryResponsible,
        operationGroupId: groupId,
        isVisibleOnline: storeData.isVisibleOnline === true,
        isPickupEnabled: storeData.isPickupEnabled !== false,
        isDeliveryEnabled: storeData.isDeliveryEnabled !== false,
        onlineOrderControlMode: normalizeOnlineOrderControlMode(
          storeData.onlineOrderControlMode || DEFAULT_ONLINE_ORDER_CONTROL_MODE
        ),
        productAvailabilityControlMode: normalizeProductAvailabilityControlMode(
          storeData.productAvailabilityControlMode
        ),
        menuCatalogOwner: normalizeMenuCatalogOwner(
          storeData.menuCatalogOwner || DEFAULT_MENU_CATALOG_OWNER
        ),
        phone: storeData.phone || null,
        email: storeData.email || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (isDeliveryResponsible) {
        await storeService.clearOtherDeliveryResponsibles(newStoreRef.id, groupId);
      } else {
        await ensureActiveDeliveryResponsibleInternal(groupId);
      }

      await rebuildOperationGroupState(groupId);
      await refreshDriverDeliveryStoreIdsForGroup(groupId);

      const newStore = await getDoc(newStoreRef);
      return toStore(newStore);
    } catch (error: any) {
      console.error('Error creating store:', error);
      throw new Error(`Failed to create store: ${error.message}`);
    }
  },

  updateStore: async (id: string, storeData: StoreFormData): Promise<void> => {
    try {
      const storeRef = doc(db, 'stores', id);
      const storeDoc = await getDoc(storeRef);

      if (!storeDoc.exists()) {
        throw new Error(`Store with ID ${id} does not exist`);
      }

      const existingStore = toStore(storeDoc);
      const previousGroupId = getStoreGroupId(existingStore);
      const nextGroupId = storeData.operationGroupId || null;
      const geoPoint = new GeoPoint(
        storeData.location.latitude,
        storeData.location.longitude
      );
      const isDeliveryResponsible = storeData.isActive
        ? storeData.isDeliveryResponsible === true
        : false;

      if (isDeliveryResponsible) {
        await storeService.clearOtherDeliveryResponsibles(id, nextGroupId);
      }

      await updateDoc(storeRef, {
        name: storeData.name,
        address: storeData.address,
        location: geoPoint,
        isActive: storeData.isActive,
        isDeliveryResponsible,
        operationGroupId: nextGroupId,
        isVisibleOnline: storeData.isVisibleOnline === true,
        isPickupEnabled: storeData.isPickupEnabled !== false,
        isDeliveryEnabled: storeData.isDeliveryEnabled !== false,
        onlineOrderControlMode: normalizeOnlineOrderControlMode(
          storeData.onlineOrderControlMode || existingStore.onlineOrderControlMode
        ),
        productAvailabilityControlMode: normalizeProductAvailabilityControlMode(
          storeData.productAvailabilityControlMode ||
            existingStore.productAvailabilityControlMode
        ),
        menuCatalogOwner: normalizeMenuCatalogOwner(
          storeData.menuCatalogOwner || existingStore.menuCatalogOwner
        ),
        phone: storeData.phone || null,
        email: storeData.email || null,
        updatedAt: serverTimestamp(),
      });

      if (!isDeliveryResponsible) {
        await ensureActiveDeliveryResponsibleInternal(nextGroupId);
      }
      if (previousGroupId && previousGroupId !== nextGroupId) {
        await ensureActiveDeliveryResponsibleInternal(previousGroupId);
      }

      await rebuildOperationGroupState(previousGroupId);
      await rebuildOperationGroupState(nextGroupId);
      await refreshDriverDeliveryStoreIdsForGroup(previousGroupId);
      await refreshDriverDeliveryStoreIdsForGroup(nextGroupId);
    } catch (error: any) {
      console.error(`Error updating store ${id}:`, error);
      throw new Error(`Failed to update store: ${error.message}`);
    }
  },

  updateOnlineOrderControlMode: async (
    id: string,
    mode: OnlineOrderControlMode
  ): Promise<void> => {
    try {
      await updateDoc(doc(db, 'stores', id), {
        onlineOrderControlMode: normalizeOnlineOrderControlMode(mode),
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error(`Error updating online order control mode for store ${id}:`, error);
      throw new Error(
        `Failed to update online order control mode: ${error.message}`
      );
    }
  },

  updateStoreControlModes: async (
    id: string,
    onlineOrderControlMode: OnlineOrderControlMode,
    productAvailabilityControlMode: ProductAvailabilityControlMode,
    menuCatalogOwner: MenuCatalogOwner
  ): Promise<void> => {
    try {
      await updateDoc(doc(db, 'stores', id), {
        onlineOrderControlMode: normalizeOnlineOrderControlMode(
          onlineOrderControlMode
        ),
        productAvailabilityControlMode: normalizeProductAvailabilityControlMode(
          productAvailabilityControlMode
        ),
        menuCatalogOwner: normalizeMenuCatalogOwner(menuCatalogOwner),
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error(`Error updating store control modes for store ${id}:`, error);
      throw new Error(`Failed to update store control modes: ${error.message}`);
    }
  },

  deleteStore: async (id: string): Promise<void> => {
    try {
      const storeDoc = await getDoc(doc(db, 'stores', id));
      if (!storeDoc.exists()) {
        return;
      }

      const store = toStore(storeDoc);
      const groupId = getStoreGroupId(store);

      await deleteDoc(doc(db, 'stores', id));
      await ensureActiveDeliveryResponsibleInternal(groupId);
      await rebuildOperationGroupState(groupId);
      await refreshDriverDeliveryStoreIdsForGroup(groupId);
    } catch (error: any) {
      console.error(`Error deleting store ${id}:`, error);
      throw new Error(`Failed to delete store: ${error.message}`);
    }
  },

  getDeliveryResponsibleStore: async (groupId?: string | null): Promise<Store | null> => {
    try {
      const stores = await listStoresSorted();
      const scopedStores = stores.filter((store) => {
        if (!store.isActive || !store.isDeliveryResponsible) {
          return false;
        }
        return getStoreGroupId(store) === (groupId || null);
      });

      if (scopedStores.length > 0) {
        return scopedStores[0];
      }

      await ensureActiveDeliveryResponsibleInternal(groupId || null);
      const refreshedStores = await listStoresSorted();
      return (
        refreshedStores.find(
          (store) =>
            store.isActive &&
            store.isDeliveryResponsible &&
            getStoreGroupId(store) === (groupId || null)
        ) || null
      );
    } catch (error: any) {
      console.error('Error getting delivery responsible store:', error);
      throw new Error(`Failed to fetch delivery responsible store: ${error.message}`);
    }
  },

  setDeliveryResponsibleStore: async (storeId: string): Promise<void> => {
    try {
      const storeDoc = await getDoc(doc(db, 'stores', storeId));
      if (!storeDoc.exists()) {
        throw new Error(`Store with ID ${storeId} does not exist`);
      }

      const store = toStore(storeDoc);
      if (!store.isActive) {
        throw new Error(`Store with ID ${storeId} is not active`);
      }

      await storeService.clearOtherDeliveryResponsibles(storeId, store.operationGroupId || null);

      await updateDoc(doc(db, 'stores', storeId), {
        isDeliveryResponsible: true,
        updatedAt: serverTimestamp(),
      });

      await rebuildOperationGroupState(store.operationGroupId || null);
    } catch (error: any) {
      console.error('Error setting delivery responsible store:', error);
      throw new Error(`Failed to set delivery responsible store: ${error.message}`);
    }
  },

  clearOtherDeliveryResponsibles: async (
    exceptStoreId: string,
    groupId?: string | null
  ): Promise<void> => {
    try {
      const responsibleStoresSnapshot = await getDocs(
        query(storesCollectionRef, where('isDeliveryResponsible', '==', true))
      );

      const batch = writeBatch(db);

      responsibleStoresSnapshot.forEach((storeDoc) => {
        const store = toStore(storeDoc);
        if (store.id === exceptStoreId) {
          return;
        }
        if (getStoreGroupId(store) !== (groupId || null)) {
          return;
        }
        batch.update(storeDoc.ref, {
          isDeliveryResponsible: false,
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();
      await rebuildOperationGroupState(groupId || null);
    } catch (error: any) {
      console.error('Error clearing delivery responsibles:', error);
      throw new Error(`Failed to clear other delivery responsibles: ${error.message}`);
    }
  },

  ensureActiveDeliveryResponsible: async (groupId?: string | null): Promise<void> => {
    try {
      await ensureActiveDeliveryResponsibleInternal(groupId || null);
    } catch (error: any) {
      console.error('Error ensuring active delivery responsible store:', error);
      throw new Error(`Failed to ensure active delivery responsible store: ${error.message}`);
    }
  },

  setDupRulesCodeHash: async (storeId: string, hash: string, updatedBy?: string): Promise<void> => {
    try {
      const storeRef = doc(db, 'stores', storeId);
      await setDoc(
        storeRef,
        {
          settings: {
            dupRulesCodeHash: hash,
            updatedAt: serverTimestamp(),
            updatedBy: updatedBy ?? null,
          },
        },
        { merge: true }
      );
    } catch (error: any) {
      console.error('Error setting dupRulesCodeHash:', error);
      throw new Error(`Failed to set settings.dupRulesCodeHash: ${error.message}`);
    }
  },
};
